import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type ContentTypeDTO, Role } from '@today/types'
import type { TypeFormView, TypeRowView, TypesViewProps } from '@today/ui-domain'
import { type Dispatch, type SetStateAction, useState } from 'react'
import { contentService } from '@/services/content.service'
import {
	EMPTY_TYPE_FORM,
	type TypeFormValues,
	typeCreateBody,
	typeErrors,
	typeToForm,
	typeUpdateBody,
} from './type-form'
import { contentKeys, useContentLocales, useContentT, useContentTypes } from './useContentQueries'

type FormState = { id: string | null; values: TypeFormValues; errors: Record<string, string>; formError: string | null }

const typeRow = (x: ContentTypeDTO): TypeRowView => ({
	id: x.id,
	code: x.code,
	name: x.name,
	isSystem: x.isSystem,
	requiresApproval: x.requiresApproval,
	approverRoles: x.approverRoles,
	forbidSelfApproval: x.forbidSelfApproval,
	requiredLocales: x.requiredLocales,
	allowLocaleFallback: x.allowLocaleFallback,
})

/** Create/update and delete, each reloading the types; a code clash lands on the code field. */
function useTypeMutations(setForm: Dispatch<SetStateAction<FormState | null>>) {
	const { fieldError } = useContentT()
	const client = useQueryClient()
	const [deleting, setDeleting] = useState<string | null>(null)
	const reload = () => client.invalidateQueries({ queryKey: contentKeys.types })
	const save = useMutation({
		mutationFn: ({ id, body }: { id: string | null; body: Record<string, unknown> }) =>
			id ? contentService.updateType(id, body) : contentService.createType(body),
		onSuccess: async () => {
			setForm(null)
			await reload()
		},
		onError: (error) => {
			const { field, message } = fieldError(error)
			setForm((f) => f && (field === 'code' ? { ...f, errors: { code: message } } : { ...f, formError: message }))
		},
	})
	const remove = useMutation({
		mutationFn: (id: string) => contentService.deleteType(id),
		onSuccess: async () => {
			setDeleting(null)
			await reload()
		},
	})
	return { save, remove, deleting, setDeleting }
}

/** `/content/types` (TO-420 content-admin-ui REQ-009): list, create/edit through the shared schemas, delete. */
export function useContentTypesAdmin(): TypesViewProps {
	const { t, errorMessage } = useContentT()
	const types = useContentTypes()
	const [form, setForm] = useState<FormState | null>(null)
	const editing = types.data?.find((x) => x.id === form?.id)
	const { locales } = useContentLocales(form?.values.requiredLocales ?? editing?.requiredLocales)
	const { save, remove, deleting, setDeleting } = useTypeMutations(setForm)
	const submit = () => {
		if (!form) return
		const body = form.id ? typeUpdateBody(form.values) : typeCreateBody(form.values)
		const found = typeErrors(body, form.id ? 'update' : 'create')
		const errors = Object.fromEntries(Object.entries(found).map(([f, key]) => [f, t(key)]))
		setForm({ ...form, errors, formError: null })
		if (Object.keys(found).length === 0) save.mutate({ id: form.id, body })
	}
	const target = types.data?.find((x) => x.id === deleting)
	return {
		t,
		rows: (types.data ?? []).map(typeRow),
		roles: Object.values(Role),
		locales,
		error: types.isError,
		form: form && { id: form.id, values: form.values as TypeFormView, errors: form.errors, formError: form.formError },
		deleting: target ? { code: target.code, error: remove.error ? errorMessage(remove.error) : null } : null,
		isBusy: save.isPending || remove.isPending,
		onNew: () => setForm({ id: null, values: EMPTY_TYPE_FORM, errors: {}, formError: null }),
		onEdit: (id) => {
			const type = types.data?.find((x) => x.id === id)
			if (type) setForm({ id, values: typeToForm(type), errors: {}, formError: null })
		},
		onDelete: (id) => {
			remove.reset()
			setDeleting(id)
		},
		onChange: (field, value) =>
			setForm((f) => f && { ...f, values: { ...f.values, [field]: value } as TypeFormValues }),
		onSubmit: submit,
		onCancel: () => setForm(null),
		onConfirmDelete: () => deleting && remove.mutate(deleting),
		onCancelDelete: () => setDeleting(null),
		onRetry: () => void types.refetch(),
	}
}
